<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<template>
  <BaseSimpleLayout class="finance-settings">
    <template #title> MCCSファイナンス別設定 </template>
    <template #button-area>
      <div class="button-area-container">
        <ScreenPermissionGate code="MCCS_FINANCE_SETTINGS-BTN_EDIT">
          <el-button
            class="finance-settings-button-update"
            type="success"
            size="large"
            @click="updateSettings"
          >
            更新
          </el-button>
        </ScreenPermissionGate>
      </div>
    </template>
    <template #main>
      <div class="filter-section">
        <span class="filter-label">ファイナンス</span>
        <el-select
          v-model="selectedFinance"
          size="large"
          value-key="id"
          placeholder="フィルター選択"
          style="width: 200px"
          @change="fetchFinanceSettings"
        >
          <el-option
            v-for="finance in finances"
            :key="finance.id"
            :label="finance.name"
            :value="finance"
          />
        </el-select>
      </div>
      <div ref="tableWrapper">
        <el-table
          ref="tableRef"
          :data="tableData"
          border
          :max-height="tableHeight"
          @row-click="toggleRowEdit"
          :row-class-name="tableRowClassName"
        >
          <el-table-column prop="property" label="項目" width="200" sortable />
          <el-table-column prop="type" label="入力形式" width="140">
            <template #default="scope">
              {{ getInputTypeLabel(scope.row.type) }}
            </template>
          </el-table-column>
          <el-table-column prop="value" label="値" min-width="250" sortable>
            <template #default="scope">
              <div style="position: relative">
                <template v-if="editingRowId === scope.row.mccsPropertiesId">
                  <el-tooltip
                    :content="rowErrors?.[scope.row.mccsPropertiesId]?.value"
                    :disabled="!rowErrors?.[scope.row.mccsPropertiesId]?.value"
                    transition="slide-fade"
                    effect="error"
                  >
                    <el-form-item
                      :inline-message="true"
                      :error="!!rowErrors?.[scope.row.mccsPropertiesId]"
                      :show-message="false"
                    >
                      <template v-if="scope.row.isDynamic">
                        <el-button
                          size="small"
                          type="primary"
                          @click.stop="openDynamicDialog(scope.row)"
                        >
                          値を設定
                        </el-button>
                      </template>
                      <template v-else>
                        <el-input
                          :type="scope.row.type === 2 ? 'number' : 'text'"
                          v-model="scope.row.value"
                          size="small"
                        />
                      </template>
                    </el-form-item>
                  </el-tooltip>
                </template>
                <template v-else>
                  <span>
                    <template v-if="scope.row.dynamic"> DYNAMIC </template>
                    <template v-else>
                      {{ scope.row.value }}
                    </template>
                  </span>
                </template>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="note" label="備考" min-width="250">
            <template #default="scope">
              <el-input
                v-if="editingRowId === scope.row.mccsPropertiesId"
                v-model="scope.row.note"
                type="textarea"
                :rows="3"
                size="small"
              />
              <span v-else>
                {{ scope.row.note }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="削除" width="80" align="center">
            <template #default="scope">
              <el-checkbox
                v-model="scope.row.toDelete"
                :key="scope.row.toDelete"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
      <DynamicValueDialog
        :visible="dynamicDialogVisible"
        :group="dynamicBuilder as any"
        @save="saveDynamicDialog"
        @cancel="cancelDynamicDialog"
      />
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import { ElLoading } from 'element-plus';
  import DynamicValueDialog from './DynamicValueDialog.vue';
  import type {
    FinanceSettingRow,
    FinancesDataState,
    MccsPropertiesData,
    MccsFinanceSettingsData
  } from '@/types/master/finance';
  import { useStore } from 'vuex';
  import { success, error } from '@/libs/notification';
  import { hasScreenCodePermission } from '@/libs/auth';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';

  const MCCS_FINANCE_SETTINGS_EDIT_CODE = 'MCCS_FINANCE_SETTINGS-BTN_EDIT';
  const store = useStore();
  // Table data: merged from finance-settings and mccs/properties
  const tableData = ref<FinanceSettingRow[]>([]);
  const originalData = ref<FinanceSettingRow[]>([]);
  const tableHeight = ref(600);
  const loadingFlag = ref(false);
  const rowErrors = ref<
    Record<string | number, Partial<Record<keyof FinanceSettingRow, string>>>
  >({});
  const editingRowId = ref<string | number | null>(0);
  const dynamicDialogVisible = ref(false);
  const dynamicDialogRow = ref<FinanceSettingRow | null>(null);
  const dynamicBuilder = ref(null);
  const selectedFinance = ref<FinancesDataState | null>(null);

  const finances = computed(() => store.state.financesMaster.listData);

  const initialize = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      // 1. Get finances from store
      await store.dispatch('financesMaster/fetch');
      if (!selectedFinance.value && finances.value.length > 0) {
        selectedFinance.value = finances.value[0];
      }
      await fetchFinanceSettings();
      tableHeight.value = window.innerHeight - 180;
      loadingFlag.value = true;
    } catch (e) {
      error('400', 'データ取得に失敗しました');
    } finally {
      loadingInstance.close();
    }
  };

  const fetchFinanceSettings = async (): Promise<void> => {
    if (!selectedFinance.value?.id) return;
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      await Promise.all([
        store.dispatch('mccsProperties/fetch'),
        store.dispatch('mccsFinanceSettings/fetch', selectedFinance.value.id)
      ]);
      const mccsFinanceSettings = store.state.mccsFinanceSettings.listData;
      const mccsProperties = store.state.mccsProperties.listData;
      // Join with settingsDefinitions
      const merged: FinanceSettingRow[] = mccsProperties
        .map((settings: MccsPropertiesData) => {
          const found = mccsFinanceSettings.find(
            (s: MccsFinanceSettingsData) => s.mccsPropertiesId === settings.id
          );
          return {
            ...found,
            property: settings.property,
            isDynamic: settings.type === 3,
            type: settings.type,
            mccsPropertiesId: settings?.id
          };
        })
        .sort((a: FinanceSettingRow, b: FinanceSettingRow) =>
          a.property.localeCompare(b.property)
        );
      tableData.value = merged;
      originalData.value = JSON.parse(JSON.stringify(merged));
    } catch (e) {
      error('400', 'ファイナンス設定取得に失敗しました');
    } finally {
      loadingInstance.close();
    }
  };

  function getInputTypeLabel(type: number) {
    const labels: Record<number, string> = {
      1: '文字',
      2: '数値',
      3: 'DYNAMIC'
    };
    return labels[type] || type.toString();
  }

  // Validation: only allow number if type is number
  function validateValue(row: FinanceSettingRow): boolean {
    if (!row) return true;
    let error = '';
    if (
      row.type === 2 &&
      row.value !== '' &&
      !/^[-+]?\d*(\.\d+)?$/.test(row.value)
    ) {
      error = '数値のみ入力可能です';
    }
    (rowErrors.value as Record<string, string>)[row.mccsPropertiesId] = error;
    return !error;
  }

  const toggleRowEdit = (
    row: FinanceSettingRow,
    _col: unknown,
    event: Event
  ) => {
    event.stopPropagation();
    if (!hasScreenCodePermission(MCCS_FINANCE_SETTINGS_EDIT_CODE)) return;
    if (editingRowId.value === row.mccsPropertiesId) return;
    editingRowId.value = row.mccsPropertiesId;
  };

  const exitEditMode = () => {
    if (editingRowId.value) {
      const row = tableData.value.find(
        (r) => r.mccsPropertiesId === editingRowId.value
      );
      if (row) validateValue(row);
    }
    editingRowId.value = null;
  };

  const handleCreate = async (newRows: FinanceSettingRow[]) => {
    try {
      await store.dispatch('mccsFinanceSettings/create', newRows);
    } catch (error: unknown) {
      // handle error if API returns error in the form of response.data.errors
      const errorResponse = error as {
        response?: { data?: { errors?: Record<string, unknown> } };
      };
      const errors = errorResponse.response?.data?.errors;
      Object.keys(errors || {}).forEach((key) => {
        const rowError = errors?.[key];
        const rowId = newRows[Number(key)].mccsPropertiesId;
        rowErrors.value[rowId] = rowError as Partial<
          Record<keyof FinanceSettingRow, string>
        >;
      });
      throw error;
    }
  };

  const handleUpdate = async (rows: FinanceSettingRow[]) => {
    try {
      await store.dispatch('mccsFinanceSettings/update', rows);
    } catch (error: unknown) {
      const errorResponse = error as {
        response?: { data?: { errors?: Record<string, unknown> } };
      };
      const errors = errorResponse.response?.data?.errors;
      Object.keys(errors || {}).forEach((key) => {
        const rowError = errors?.[key];
        const rowId = rows[Number(key)].mccsPropertiesId;
        rowErrors.value[rowId] = rowError as Partial<
          Record<keyof FinanceSettingRow, string>
        >;
      });
      throw error;
    }
  };

  const updateSettings = async (): Promise<void> => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      // Prepare data
      const toCreate: FinanceSettingRow[] = [];
      const toUpdate: FinanceSettingRow[] = [];
      tableData.value.forEach((row) => {
        const orig = originalData.value.find(
          (o) => o.mccsPropertiesId === row.mccsPropertiesId
        );
        if (!row.toDelete && (row.value ?? '') !== '' && !row.id) {
          // New row
          toCreate.push({
            financeId: selectedFinance.value?.id,
            mccsPropertiesId: row.mccsPropertiesId,
            value: row.value,
            note: row.note
          } as FinanceSettingRow);
        } else if (row.id) {
          // Existing row
          if (row.toDelete) {
            // Mark as deleted: set value/note to ''
            toUpdate.push({
              id: row.id,
              mccsPropertiesId: row.mccsPropertiesId,
              value: '',
              note: ''
            } as FinanceSettingRow);
          } else if (
            orig &&
            (orig.value !== row.value || orig.note !== row.note)
          ) {
            toUpdate.push({
              id: row.id,
              mccsPropertiesId: row.mccsPropertiesId,
              value: row.value,
              note: row.note
            } as FinanceSettingRow);
          }
        }
      });
      // Call APIs
      if (toCreate.length > 0) {
        await handleCreate(toCreate);
      }
      if (toUpdate.length > 0) {
        await handleUpdate(toUpdate);
      }
      await fetchFinanceSettings();
      await nextTick();
      success('Success', 'ファイナンス設定が正常に更新されました');
      editingRowId.value = null;
    } catch (e) {
      error('400', '更新中にエラーが発生しました');
    } finally {
      loadingInstance.close();
    }
  };

  const tableRowClassName = ({ row }: { row: FinanceSettingRow }) => {
    return (rowErrors.value as Record<string, string>)[row.mccsPropertiesId]
      ? 'row-error'
      : '';
  };

  const openDynamicDialog = (row: FinanceSettingRow) => {
    dynamicDialogRow.value = row;
    dynamicBuilder.value = row.value
      ? JSON.parse(row.value)
      : { 1: { combinator: 'AND', rules: [] } };
    dynamicDialogVisible.value = true;
  };

  const saveDynamicDialog = (group: unknown) => {
    if (dynamicDialogRow.value) {
      (dynamicDialogRow.value as FinanceSettingRow).dynamicData = group;
      (dynamicDialogRow.value as FinanceSettingRow).value =
        JSON.stringify(group);
      dynamicDialogVisible.value = false;
    }
  };

  const cancelDynamicDialog = () => {
    dynamicDialogVisible.value = false;
  };

  const handleClickOutside = () => {
    exitEditMode();
  };

  onMounted(() => {
    initialize();
    document.addEventListener('click', handleClickOutside);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<style lang="scss">
  .el-popper.is-error {
    /* Set padding to ensure the height is 32px */
    padding: 6px 12px;
    background: var(--el-bg-color-overlay);
    border: 1px solid var(--el-border-color-light);
    color: red;
  }

  .el-popper.is-error .el-popper__arrow::before {
    border: 1px solid var(--el-border-color-light);
    background: var(--el-bg-color-overlay);
    right: 0;
  }
  .finance-settings {
    .filter-label {
      font-weight: 600;
      font-size: 16px;
      color: #303133;
      margin-right: 10px;
      vertical-align: middle;
    }

    .el-form-item {
      margin-bottom: 0;
      position: relative;
    }
    .el-table {
      width: 100%;
      margin: 10px auto 0;

      thead th {
        background: #f5f7fa;
      }

      td {
        vertical-align: middle;
        padding: 8px;
      }

      .el-input {
        width: 100%;
        margin: 0;

        .el-input__wrapper {
          height: 32px;
          display: flex;
          align-items: center;
        }

        .el-input__inner {
          height: 32px;
          line-height: 30px;
        }
      }

      .el-select {
        width: 100%;

        .el-input__wrapper {
          height: 32px;
          display: flex;
          align-items: center;
        }

        .el-input__inner {
          height: 32px;
          line-height: 32px;
          text-align: left;
        }
      }

      .el-button {
        height: 32px;
        padding: 0 12px;
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      tbody tr {
        cursor: pointer;

        &:hover {
          background-color: #f5f7fa;
        }
      }
    }

    .el-button {
      font-weight: 600;
    }

    .add-row-container {
      margin-top: 20px;
      text-align: center;
    }

    .finance-settings-button-update {
      margin-left: 10px;
    }

    .button-area-container {
      display: flex;
      align-items: center;
      justify-content: end;
      gap: 10px;
    }

    .filter-section {
      display: flex;
      align-items: center;
      margin-top: 32px;
    }
  }
  .row-error {
    background-color: #fff0f0 !important;
  }
  .is-error .el-input__wrapper {
    border-color: #f56c6c !important;
    background: #fff !important;
  }
  .input-error-message {
    color: #f56c6c;
    font-size: 12px;
    margin: 2px 0 0 4px;
    padding: 0;
    line-height: 1.2;
    position: absolute;
    z-index: 2;
  }
  .el-table__row .el-input {
    position: relative;
  }
  .builder-group {
    border: 1px solid #e0e0e0;
    padding: 12px;
    margin-bottom: 10px;
    background: #fafbfc;
  }
  .builder-group-header {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
  }
  .builder-rule,
  .builder-nested-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }
</style>
